import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "@/components/ui/modal";
import { UnitTableCategoryFilter } from "@/components/controls/filter/UnitTableCategoryFilter";
import { UnitTableFeatureFilter } from "@/components/controls/filter/UnitTableFeatureFilter";
import { UnitTableFacilityFilter } from "@/components/controls/filter/UnitTableFacilityFilter";
import { UnitTableFeaturedFilter } from "@/components/controls/filter/UnitTableFeaturedFilter";
import { Button } from "@/components/ui/button";
import { useFacilities } from "@/hooks/useFacilities";
import type { UnitFeature, UnitCategory } from "@/types";
import {
    selectSelectedFeatures,
    selectSelectedCategories,
    selectSelectedFacilities,
    selectSelectedFeatured,
    setSelectedFeatures,
    setSelectedCategories,
    setSelectedFacilities,
    setSelectedFeatured,
} from "@/features/filters/filtersSlice";
interface UnitTableFiltersProps {
    features: UnitFeature[];
    categories: UnitCategory[];
}

export const UnitTableFilters = ({
    features,
    categories,
}: UnitTableFiltersProps) => {
    const dispatch = useDispatch();
    const { facilities } = useFacilities();

    // Redux state (applied filters)
    const appliedFeatures = useSelector(selectSelectedFeatures);
    const appliedCategories = useSelector(selectSelectedCategories);
    const appliedFacilities = useSelector(selectSelectedFacilities);
    const appliedFeatured = useSelector(selectSelectedFeatured);

    // Local draft state (pending filters)
    const [draftFeatures, setDraftFeatures] = useState<number[]>([]);
    const [draftCategories, setDraftCategories] = useState<number[]>([]);
    const [draftFacilities, setDraftFacilities] = useState<number[]>([]);
    const [draftFeatured, setDraftFeatured] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState(false);

    // Initialize draft state from Redux when modal opens
    useEffect(() => {
        if (isOpen) {
            setDraftFeatures(appliedFeatures);
            setDraftCategories(appliedCategories);
            setDraftFacilities(appliedFacilities);
            setDraftFeatured(appliedFeatured);
        }
    }, [isOpen, appliedFeatures, appliedCategories, appliedFacilities, appliedFeatured]);

    const handleClearAll = () => {
        dispatch(setSelectedFeatures([]));
        dispatch(setSelectedCategories([]));
        dispatch(setSelectedFacilities([]));
        dispatch(setSelectedFeatured(false));
        setIsOpen(false);
    };

    const handleApply = () => {
        dispatch(setSelectedFeatures(draftFeatures));
        dispatch(setSelectedCategories(draftCategories));
        dispatch(setSelectedFacilities(draftFacilities));
        dispatch(setSelectedFeatured(draftFeatured));
        setIsOpen(false);
    };

    // Count of applied filters (not draft)
    const filterCount =
        appliedFeatures.length +
        appliedCategories.length +
        appliedFacilities.length +
        (appliedFeatured ? 1 : 0);

    return (
        <>
            <Button
                variant={"outline"}
                className="tw:relative"
                onClick={() => setIsOpen(true)}
            >
                Filters
                {filterCount > 0 && (
                    <span className="tw:ml-2 tw:inline-flex tw:items-center tw:justify-center tw:min-w-5 tw:h-5 tw:aspect-square tw:text-xs tw:font-medium tw:rounded-full tw:bg-primary tw:text-primary-foreground">
                        {filterCount}
                    </span>
                )}
            </Button>
            <Modal
                open={isOpen}
                onClose={() => setIsOpen(false)}
                className="se-unit-table se-unit-table-filters"
            >
                <h4 className="tw:font-bold tw:mb-1">Unit Filter</h4>
                <UnitTableFacilityFilter
                    facilities={facilities}
                    selectedFacilities={draftFacilities}
                    onSelectionChange={setDraftFacilities}
                />
                <UnitTableFeatureFilter
                    features={features}
                    selectedFeatures={draftFeatures}
                    onSelectionChange={setDraftFeatures}
                />
                <UnitTableCategoryFilter
                    categories={categories}
                    selectedCategories={draftCategories}
                    onSelectionChange={setDraftCategories}
                />
                <UnitTableFeaturedFilter
                    selectedFeatured={draftFeatured}
                    onSelectionChange={setDraftFeatured}
                />
                <div className="tw:flex tw:justify-between tw:items-center">
                    <Button
                        className="btn btn-outline-primary"
                        onClick={handleClearAll}
                    >
                        Clear All
                    </Button>
                    <Button
                        className="btn btn-primary"
                        onClick={handleApply}
                    >
                        Apply Filters
                    </Button>
                </div>
            </Modal>
        </>
    );
};
